/**
 * Ledger Entry Editor - Mutation Services
 *
 * Typed wrappers for all ledger entry mutations.
 * Each wrapper preserves exact GraphQL variable names and schema arguments.
 * Uses gqlRequest from graphql/generic.ts for execution.
 *
 * Compatible with react-hook-form: services accept explicit payload objects
 * and return raw server results for reconciliation by the dirty/partial-success engine.
 */

import gqlRequest from 'graphql/generic';
import {
  CreateOrderLedgerEntryPayload,
  UpdateOrderLedgerEntryPayload,
  DeleteOrderLedgerEntryPayload,
  CreateOrderLedgerAssociationPayload,
  UpdateOrderLedgerAssociationPayload,
  DeleteOrderLedgerAssociationPayload,
  HelpOrderLedgerEntryPartiesPayload,
  HelpOrderLinkedEntryPayload,
  UpdateOrderLedgerPostedPayload,
  DeleteOrderLedgerRolePayload,
  OrderMutationReturn,
  OrderLedgerEntryUpdateContent,
  OrderLedgerAssociationContent,
  OrderEntriesContent,
  OrderLinkedEntryContent,
} from '../contracts';

// ============================================================================
// ENTRY LIFECYCLE MUTATIONS
// ============================================================================

/**
 * Create a new ledger entry.
 * Preserves exact schema: _id, amount, code, description, kind, and optional fields.
 */
export const createOrderLedgerEntry = (payload: CreateOrderLedgerEntryPayload) => {
  const mutation = `
    mutation CreateOrderLedgerEntry(
      $id: ID!
      $amount: Float!
      $code: OrderLedgerCodes!
      $description: String!
      $kind: OrderLedgerKinds!
      $entryDate: String
      $letter: OrderLedgerCharges
      $number: Int
      $months: Int
      $months_advanced: Int
      $annuality: Float
      $mortgage_id: String
      $filters: OrderFilters
    ) {
      createOrderLedgerEntry(
        _id: $id
        amount: $amount
        code: $code
        description: $description
        kind: $kind
        entry_date: $entryDate
        letter: $letter
        number: $number
        months: $months
        months_advanced: $months_advanced
        annuality: $annuality
        mortgage_id: $mortgage_id
        filters: $filters
      ) {
        _id
      }
    }
  `;

  type Variables = {
    id: string;
    amount: number;
    code: string;
    description: string;
    kind: string;
    entryDate?: string;
    letter?: string;
    number?: number;
    months?: number;
    months_advanced?: number;
    annuality?: number;
    mortgage_id?: string;
    filters?: Record<string, unknown>;
  };

  const variables: Variables = {
    id: payload._id,
    amount: payload.amount,
    code: payload.code,
    description: payload.description,
    kind: payload.kind,
    entryDate: payload.entry_date,
    letter: payload.letter,
    number: payload.number,
    months: payload.months,
    months_advanced: payload.months_advanced,
    annuality: payload.annuality,
    mortgage_id: payload.mortgage_id,
    filters: payload.filters,
  };

  return gqlRequest<OrderMutationReturn, Variables>(mutation, variables);
};

/**
 * Update an existing ledger entry.
 * Preserves exact schema: _id, accounting_id, data (OrderLedgerEntryUpdateContent).
 */
export const updateOrderLedgerEntry = (payload: UpdateOrderLedgerEntryPayload) => {
  const mutation = `
    mutation UpdateOrderLedgerEntry(
      $id: ID!
      $accounting_id: ID!
      $data: OrderLedgerEntryUpdateContent!
      $filters: OrderFilters
    ) {
      updateOrderLedgerEntry(
        _id: $id
        accounting_id: $accounting_id
        data: $data
        filters: $filters
      ) {
        _id
      }
    }
  `;

  type Variables = {
    id: string;
    accounting_id: string;
    data: OrderLedgerEntryUpdateContent;
    filters?: Record<string, unknown>;
  };

  const variables: Variables = {
    id: payload._id,
    accounting_id: payload.accounting_id,
    data: payload.data,
    filters: payload.filters,
  };

  return gqlRequest<OrderMutationReturn, Variables>(mutation, variables);
};

/**
 * Delete a ledger entry.
 * Preserves exact schema: _id, accounting_id.
 */
export const deleteOrderLedgerEntry = (payload: DeleteOrderLedgerEntryPayload) => {
  const mutation = `
    mutation DeleteOrderLedgerEntry(
      $id: ID!
      $accounting_id: ID!
      $filters: OrderFilters
    ) {
      deleteOrderLedgerEntry(
        _id: $id
        accounting_id: $accounting_id
        filters: $filters
      ) {
        _id
      }
    }
  `;

  type Variables = {
    id: string;
    accounting_id: string;
    filters?: Record<string, unknown>;
  };

  const variables: Variables = {
    id: payload._id,
    accounting_id: payload.accounting_id,
    filters: payload.filters,
  };

  return gqlRequest<OrderMutationReturn, Variables>(mutation, variables);
};

// ============================================================================
// ASSOCIATION LIFECYCLE MUTATIONS
// ============================================================================

/**
 * Create a party association for a ledger entry.
 * Preserves exact schema: _id, accounting_id, data, kind, party_id.
 */
export const createOrderLedgerAssociation = (payload: CreateOrderLedgerAssociationPayload) => {
  const mutation = `
    mutation CreateOrderLedgerAssociation(
      $id: ID!
      $accounting_id: ID!
      $data: OrderLedgerAssociationContent!
      $kind: String!
      $party_id: ID!
      $filters: OrderFilters
    ) {
      createOrderLedgerAssociation(
        _id: $id
        accounting_id: $accounting_id
        data: $data
        kind: $kind
        party_id: $party_id
        filters: $filters
      ) {
        _id
      }
    }
  `;

  type Variables = {
    id: string;
    accounting_id: string;
    data: OrderLedgerAssociationContent;
    kind: string;
    party_id: string;
    filters?: Record<string, unknown>;
  };

  const variables: Variables = {
    id: payload._id,
    accounting_id: payload.accounting_id,
    data: payload.data,
    kind: payload.kind,
    party_id: payload.party_id,
    filters: payload.filters,
  };

  return gqlRequest<OrderMutationReturn, Variables>(mutation, variables);
};

/**
 * Update a party association for a ledger entry.
 * Preserves exact schema: _id, accounting_id, data, kind, party_id.
 */
export const updateOrderLedgerAssociation = (payload: UpdateOrderLedgerAssociationPayload) => {
  const mutation = `
    mutation UpdateOrderLedgerAssociation(
      $id: ID!
      $accounting_id: ID!
      $data: OrderLedgerAssociationContent!
      $kind: String!
      $party_id: ID!
      $filters: OrderFilters
    ) {
      updateOrderLedgerAssociation(
        _id: $id
        accounting_id: $accounting_id
        data: $data
        kind: $kind
        party_id: $party_id
        filters: $filters
      ) {
        _id
      }
    }
  `;

  type Variables = {
    id: string;
    accounting_id: string;
    data: OrderLedgerAssociationContent;
    kind: string;
    party_id: string;
    filters?: Record<string, unknown>;
  };

  const variables: Variables = {
    id: payload._id,
    accounting_id: payload.accounting_id,
    data: payload.data,
    kind: payload.kind,
    party_id: payload.party_id,
    filters: payload.filters,
  };

  return gqlRequest<OrderMutationReturn, Variables>(mutation, variables);
};

/**
 * Delete a party association from a ledger entry.
 * Preserves exact schema: _id, accounting_id, kind, party_id.
 */
export const deleteOrderLedgerAssociation = (payload: DeleteOrderLedgerAssociationPayload) => {
  const mutation = `
    mutation DeleteOrderLedgerAssociation(
      $id: ID!
      $accounting_id: ID!
      $kind: String!
      $party_id: ID!
      $filters: OrderFilters
    ) {
      deleteOrderLedgerAssociation(
        _id: $id
        accounting_id: $accounting_id
        kind: $kind
        party_id: $party_id
        filters: $filters
      ) {
        _id
      }
    }
  `;

  type Variables = {
    id: string;
    accounting_id: string;
    kind: string;
    party_id: string;
    filters?: Record<string, unknown>;
  };

  const variables: Variables = {
    id: payload._id,
    accounting_id: payload.accounting_id,
    kind: payload.kind,
    party_id: payload.party_id,
    filters: payload.filters,
  };

  return gqlRequest<OrderMutationReturn, Variables>(mutation, variables);
};

// ============================================================================
// HELPER MUTATIONS - PARTY & LINKED ENTRY OPERATIONS
// ============================================================================

/**
 * Modify involved party information for all related ledger entries.
 * Replaces existing associations with provided data kinds.
 * Preserves exact schema: _id, data (OrderEntriesContent[]).
 */
export const helpOrderLedgerEntryParties = (payload: HelpOrderLedgerEntryPartiesPayload) => {
  const mutation = `
    mutation HelpOrderLedgerEntryParties(
      $id: ID!
      $data: [OrderEntriesContent!]!
      $filters: OrderFilters
    ) {
      helpOrderLedgerEntryParties(
        _id: $id
        data: $data
        filters: $filters
      ) {
        _id
      }
    }
  `;

  type Variables = {
    id: string;
    data: OrderEntriesContent[];
    filters?: Record<string, unknown>;
  };

  const variables: Variables = {
    id: payload._id,
    data: payload.data,
    filters: payload.filters,
  };

  return gqlRequest<OrderMutationReturn, Variables>(mutation, variables);
};

/**
 * Create linked entries with charge information.
 * Preserves exact schema: _id, code, description, entry_date, kind, targets, posted.
 */
export const helpOrderLinkedEntry = (payload: HelpOrderLinkedEntryPayload) => {
  const mutation = `
    mutation HelpOrderLinkedEntry(
      $id: ID!
      $code: OrderLedgerCodes!
      $description: String!
      $entry_date: String!
      $kind: OrderLedgerKinds!
      $targets: [OrderLinkedEntryContent!]!
      $posted: Boolean
      $filters: OrderFilters
    ) {
      helpOrderLinkedEntry(
        _id: $id
        code: $code
        description: $description
        entry_date: $entry_date
        kind: $kind
        targets: $targets
        posted: $posted
        filters: $filters
      ) {
        _id
      }
    }
  `;

  type Variables = {
    id: string;
    code: string;
    description: string;
    entry_date: string;
    kind: string;
    targets: OrderLinkedEntryContent[];
    posted?: boolean;
    filters?: Record<string, unknown>;
  };

  const variables: Variables = {
    id: payload._id,
    code: payload.code,
    description: payload.description,
    entry_date: payload.entry_date,
    kind: payload.kind,
    targets: payload.targets,
    posted: payload.posted,
    filters: payload.filters,
  };

  return gqlRequest<OrderMutationReturn, Variables>(mutation, variables);
};

// ============================================================================
// STATUS & ROLE MUTATIONS
// ============================================================================

/**
 * Update posted status of a ledger entry.
 * Preserves exact schema: _id, accounting_id, posted.
 */
export const updateOrderLedgerPosted = (payload: UpdateOrderLedgerPostedPayload) => {
  const mutation = `
    mutation UpdateOrderLedgerPosted(
      $id: ID!
      $accounting_id: ID!
      $posted: Boolean!
    ) {
      updateOrderLedgerPosted(
        _id: $id
        accounting_id: $accounting_id
        posted: $posted
      ) {
        _id
      }
    }
  `;

  type Variables = {
    id: string;
    accounting_id: string;
    posted: boolean;
  };

  const variables: Variables = {
    id: payload._id,
    accounting_id: payload.accounting_id,
    posted: payload.posted,
  };

  return gqlRequest<OrderMutationReturn, Variables>(mutation, variables);
};

/**
 * Delete roles from ledger entries.
 * Preserves exact schema: _id, accounting_ids (array), role.
 */
export const deleteOrderLedgerRole = (payload: DeleteOrderLedgerRolePayload) => {
  const mutation = `
    mutation DeleteOrderLedgerRole(
      $id: ID!
      $accounting_ids: [ID!]!
      $role: String!
      $filters: OrderFilters
    ) {
      deleteOrderLedgerRole(
        _id: $id
        accounting_ids: $accounting_ids
        role: $role
        filters: $filters
      ) {
        _id
      }
    }
  `;

  type Variables = {
    id: string;
    accounting_ids: string[];
    role: string;
    filters?: Record<string, unknown>;
  };

  const variables: Variables = {
    id: payload._id,
    accounting_ids: payload.accounting_ids,
    role: payload.role,
    filters: payload.filters,
  };

  return gqlRequest<OrderMutationReturn, Variables>(mutation, variables);
};
